import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ArrowLeft, Crown, User } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CCY_SYMBOL = { USD: '$', EUR: '€', GBP: '£', JPY: '¥' };
const fmtMoney = (n, ccy = 'USD') => {
  const s = CCY_SYMBOL[(ccy || 'USD').toUpperCase()] || '';
  const v = Number(n || 0);
  return `${v < 0 ? '-' : ''}${s}${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtMetric = (n, ccy) => (n === null ? 'Unavailable' : fmtMoney(n, ccy));
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

// Same tier boundaries as the client Dashboard and public Home page.
const tierOf = (bal) => {
  const b = Number(bal || 0);
  if (b >= 250000) return 'VIP';
  if (b >= 50000) return 'Platinum';
  if (b >= 5000) return 'Premium';
  return 'Standard';
};

const Section = ({ title, children, right }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
    <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
      <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider">{title}</h3>
      {right}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const PagedTable = ({ fetcher, columns, rowKey, emptyLabel, dataTestId }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const limit = 25;
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetcher(skip, limit);
      setRows(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load'); setRows([]); }
    finally { setLoading(false); }
  }, [fetcher, skip]);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid={dataTestId}>
      {loading ? (
        <div className="text-slate-500 text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-slate-500 text-sm">{emptyLabel}</div>
      ) : (
        <>
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 text-left uppercase tracking-wider text-slate-600">
                {columns.map((c) => (
                  <th key={c.key} className={`p-2 ${c.align === 'right' ? 'text-right' : ''}`}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={rowKey(r)} className="border-b border-slate-100">
                  {columns.map((c) => (
                    <td key={c.key} className={`p-2 ${c.align === 'right' ? 'text-right tabular-nums' : ''}`}>{c.render(r)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
            <span>Showing {skip + 1} – {skip + rows.length} of {total}</span>
            <div className="flex gap-2">
              <button className="px-2 py-1 rounded border border-slate-300 disabled:opacity-40" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))}>Prev</button>
              <button className="px-2 py-1 rounded border border-slate-300 disabled:opacity-40" disabled={skip + limit >= total} onClick={() => setSkip(skip + limit)}>Next</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const AdminClientDetail = () => {
  const { id } = useParams();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/clients/${id}`);
      setDetail(r.data);
      // Fetch counts in parallel for tab labels
      const [posC, histC, depC, witC, ledC] = await Promise.all([
        axios.get(`${API}/admin/clients/${id}/positions?limit=1`),
        axios.get(`${API}/admin/clients/${id}/history?limit=1`),
        axios.get(`${API}/admin/clients/${id}/deposits?limit=1`),
        axios.get(`${API}/admin/clients/${id}/withdrawals?limit=1`),
        axios.get(`${API}/admin/clients/${id}/ledger?limit=1`),
      ]);
      setCounts({
        positions: posC.data.total,
        history: histC.data.total,
        deposits: depC.data.total,
        withdrawals: witC.data.total,
        ledger: ledC.data.total,
      });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load client'); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-6 text-slate-500">Loading client…</div>;
  if (!detail) return <div className="p-6 text-slate-500">Client not found</div>;

  const { client, accounts, aggregate_open_pl, last_login } = detail;
  const account = accounts && accounts[0];
  const ccy = account?.currency || 'USD';
  const tier = tierOf(account?.balance);

  return (
    <div className="p-6 space-y-6" data-testid="admin-client-detail">
      <Link to="/admin/clients" className="inline-flex items-center gap-1 text-sm text-brand-primary hover:underline">
        <ArrowLeft size={14} /> Back to Clients
      </Link>

      <div className="bg-gradient-to-r from-brand-primary to-brand-accent text-white p-6 rounded-xl flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest opacity-80">
            <User size={14} /> Client
          </div>
          <div className="text-2xl font-black mt-1" data-testid="client-fullname">{client.full_name || client.email}</div>
          <div className="text-sm opacity-80 mt-1">{client.email}</div>
        </div>
        <div className="text-right">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest opacity-80 justify-end">
            <Crown size={14} weight="fill" /> {tier}
          </div>
          <div className="text-xl font-bold tabular-nums mt-1">{fmtMoney(account?.balance, ccy)}</div>
          <div className="text-xs opacity-80 mt-1">Equity: {fmtMetric(account?.equity === undefined ? account?.balance : account.equity, ccy)}</div>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="positions" data-testid="tab-positions">Positions ({counts.positions || 0})</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Trade History ({counts.history || 0})</TabsTrigger>
          <TabsTrigger value="deposits" data-testid="tab-deposits">Deposits ({counts.deposits || 0})</TabsTrigger>
          <TabsTrigger value="withdrawals" data-testid="tab-withdrawals">Withdrawals ({counts.withdrawals || 0})</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-ledger">Ledger ({counts.ledger || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid md:grid-cols-2 gap-5 mt-4">
            <Section title="Profile">
              <div className="grid grid-cols-2 gap-y-2 text-sm">
                <div className="text-slate-500">Full Name</div><div className="font-semibold">{client.full_name || '—'}</div>
                <div className="text-slate-500">Email</div><div>{client.email}</div>
                <div className="text-slate-500">Status</div><div>{client.is_active ? 'Active' : 'Suspended'}</div>
                <div className="text-slate-500">KYC Status</div><div>{client.kyc_status || 'PENDING'}</div>
                <div className="text-slate-500">Registered</div><div>{fmtDate(client.created_at)}</div>
                <div className="text-slate-500">Last Login</div><div>{fmtDate(last_login?.logged_in_at)}</div>
                <div className="text-slate-500">Last IP</div><div className="truncate">{last_login?.ip || '—'}</div>
              </div>
            </Section>
            <Section title="Trading Account">
              <div className="grid grid-cols-2 gap-y-2 text-sm">
                <div className="text-slate-500">Account Currency</div><div className="font-semibold">{ccy}</div>
                <div className="text-slate-500">Account Type</div><div className="font-semibold">{tier}</div>
                <div className="text-slate-500">Balance</div><div>{fmtMoney(account?.balance, ccy)}</div>
                <div className="text-slate-500">Equity</div><div data-testid="detail-equity">{fmtMetric(account?.equity === undefined ? account?.balance : account.equity, ccy)}</div>
                <div className="text-slate-500">Open P/L</div>
                <div className={`font-semibold ${(aggregate_open_pl || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmtMetric(aggregate_open_pl, ccy)}</div>
                <div className="text-slate-500">Margin</div><div data-testid="detail-margin">{fmtMetric(account?.margin, ccy)}</div>
                <div className="text-slate-500">Free Margin</div><div data-testid="detail-free-margin">{fmtMetric(account?.free_margin, ccy)}</div>
                <div className="text-slate-500">Leverage</div><div>1:{account?.leverage || 100}</div>
              </div>
            </Section>
          </div>
        </TabsContent>

        <TabsContent value="positions">
          <Section title="Open Positions">
            <PagedTable
              dataTestId="admin-positions-table"
              fetcher={(s, l) => axios.get(`${API}/admin/clients/${id}/positions?skip=${s}&limit=${l}`)}
              rowKey={(r) => r.id}
              emptyLabel="No open positions"
              columns={[
                { key: 'symbol', label: 'Symbol', render: (r) => <span className="font-semibold">{r.symbol}</span> },
                { key: 'direction', label: 'Dir', render: (r) => <span className={r.direction === 'BUY' ? 'text-green-700' : 'text-red-700'}>{r.direction}</span> },
                { key: 'volume', label: 'Volume', align: 'right', render: (r) => r.volume },
                { key: 'open_price', label: 'Open', align: 'right', render: (r) => Number(r.open_price || 0).toFixed(5) },
                { key: 'current_price', label: 'Current', align: 'right', render: (r) => (r.current_price == null ? '—' : Number(r.current_price).toFixed(5)) },
                { key: 'profit_loss', label: 'P/L', align: 'right', render: (r) => <span className={(r.profit_loss || 0) >= 0 ? 'text-green-700' : 'text-red-700'}>{fmtMetric(r.profit_loss, r.account_currency || ccy)}</span> },
                { key: 'sl', label: 'SL', align: 'right', render: (r) => r.stop_loss ?? '—' },
                { key: 'tp', label: 'TP', align: 'right', render: (r) => r.take_profit ?? '—' },
                { key: 'leverage', label: 'Lev', align: 'right', render: (r) => `1:${r.leverage || account?.leverage || 100}` },
                { key: 'opened_at', label: 'Opened', render: (r) => fmtDate(r.opened_at) },
              ]}
            />
          </Section>
        </TabsContent>

        <TabsContent value="history">
          <Section title="Trade History (closed)">
            <PagedTable
              dataTestId="admin-history-table"
              fetcher={(s, l) => axios.get(`${API}/admin/clients/${id}/history?skip=${s}&limit=${l}`)}
              rowKey={(r) => r.id}
              emptyLabel="No closed trades yet"
              columns={[
                { key: 'symbol', label: 'Symbol', render: (r) => <span className="font-semibold">{r.symbol}</span> },
                { key: 'direction', label: 'Dir', render: (r) => <span className={r.direction === 'BUY' ? 'text-green-700' : 'text-red-700'}>{r.direction}</span> },
                { key: 'volume', label: 'Volume', align: 'right', render: (r) => r.volume },
                { key: 'open_price', label: 'Open', align: 'right', render: (r) => Number(r.open_price || 0).toFixed(5) },
                { key: 'close_price', label: 'Close', align: 'right', render: (r) => Number(r.close_price ?? r.current_price ?? 0).toFixed(5) },
                { key: 'opened_at', label: 'Opened', render: (r) => fmtDate(r.opened_at) },
                { key: 'closed_at', label: 'Closed', render: (r) => fmtDate(r.closed_at) },
                { key: 'pl', label: 'P/L', align: 'right', render: (r) => <span className={(r.profit_loss || 0) >= 0 ? 'text-green-700' : 'text-red-700'}>{fmtMoney(r.profit_loss, r.account_currency || ccy)}</span> },
                { key: 'comm', label: 'Commission', align: 'right', render: (r) => fmtMoney(r.commission, r.account_currency || ccy) },
                { key: 'swap', label: 'Swap', align: 'right', render: (r) => fmtMoney(r.swap, r.account_currency || ccy) },
              ]}
            />
          </Section>
        </TabsContent>

        <TabsContent value="deposits">
          <Section title="Deposits">
            <PagedTable
              dataTestId="admin-deposits-table"
              fetcher={(s, l) => axios.get(`${API}/admin/clients/${id}/deposits?skip=${s}&limit=${l}`)}
              rowKey={(r) => r.transaction_id || r.id}
              emptyLabel="No deposits"
              columns={[
                { key: 'id', label: 'Transaction ID', render: (r) => <span className="font-mono">{(r.transaction_id || r.id || '').slice(0, 14)}</span> },
                { key: 'amount', label: 'Amount', align: 'right', render: (r) => fmtMoney(r.amount, r.currency) },
                { key: 'ccy', label: 'Currency', render: (r) => r.currency },
                { key: 'method', label: 'Method', render: (r) => r.payment_method || '—' },
                { key: 'status', label: 'Status', render: (r) => r.status },
                { key: 'created', label: 'Date', render: (r) => fmtDate(r.created_at) },
                { key: 'processed', label: 'Processed', render: (r) => fmtDate(r.processed_at || r.approved_at || r.rejected_at) },
                { key: 'proof', label: 'Proof', render: (r) => (
                  r.metadata?.proof_storage_path ? (
                    <button
                      className="text-xs text-blue-700 underline"
                      data-testid={`admin-view-proof-${r.transaction_id || r.id}`}
                      onClick={async () => {
                        try {
                          const resp = await axios.get(`${API}/transactions/${r.transaction_id || r.id}/proof/download`, { responseType: 'blob' });
                          const url = URL.createObjectURL(resp.data);
                          window.open(url, '_blank', 'noopener,noreferrer');
                          setTimeout(() => URL.revokeObjectURL(url), 60_000);
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || 'Download failed');
                        }
                      }}
                    >View</button>
                  ) : (r.metadata?.proof_filename ? <span className="text-xs text-slate-400">{r.metadata.proof_filename}</span> : <span className="text-slate-300">—</span>)
                ) },
              ]}
            />
          </Section>
        </TabsContent>

        <TabsContent value="withdrawals">
          <Section title="Withdrawals">
            <PagedTable
              dataTestId="admin-withdrawals-table"
              fetcher={(s, l) => axios.get(`${API}/admin/clients/${id}/withdrawals?skip=${s}&limit=${l}`)}
              rowKey={(r) => r.transaction_id || r.id}
              emptyLabel="No withdrawals"
              columns={[
                { key: 'id', label: 'Transaction ID', render: (r) => <span className="font-mono">{(r.transaction_id || r.id || '').slice(0, 14)}</span> },
                { key: 'amount', label: 'Amount', align: 'right', render: (r) => fmtMoney(r.amount, r.currency) },
                { key: 'ccy', label: 'Currency', render: (r) => r.currency },
                { key: 'method', label: 'Method', render: (r) => r.payment_method || r.withdrawal_method || '—' },
                { key: 'status', label: 'Status', render: (r) => r.status },
                { key: 'created', label: 'Date', render: (r) => fmtDate(r.created_at) },
                { key: 'processed', label: 'Processed', render: (r) => fmtDate(r.processed_at || r.approved_at || r.rejected_at) },
                { key: 'dest', label: 'Destination', render: (r) => (r.metadata?.wallet_address || r.metadata?.bank_account || '—') },
              ]}
            />
          </Section>
        </TabsContent>

        <TabsContent value="ledger">
          <Section title="Complete Ledger / Transactions">
            <PagedTable
              dataTestId="admin-ledger-table"
              fetcher={(s, l) => axios.get(`${API}/admin/clients/${id}/ledger?skip=${s}&limit=${l}`)}
              rowKey={(r) => r.transaction_id || r.id}
              emptyLabel="No transactions"
              columns={[
                { key: 'id', label: 'Transaction ID', render: (r) => <span className="font-mono">{(r.transaction_id || r.id || '').slice(0, 14)}</span> },
                { key: 'type', label: 'Type', render: (r) => r.type },
                { key: 'amount', label: 'Amount', align: 'right', render: (r) => fmtMoney(r.amount, r.currency) },
                { key: 'ccy', label: 'Currency', render: (r) => r.currency },
                { key: 'status', label: 'Status', render: (r) => r.status },
                { key: 'created', label: 'Date', render: (r) => fmtDate(r.created_at) },
                { key: 'bal_after', label: 'Balance After', align: 'right', render: (r) => (r.balance_after != null ? fmtMoney(r.balance_after, r.currency) : '—') },
              ]}
            />
          </Section>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminClientDetail;
